import { memo, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { NotificationsCenter } from './components/NotificationsCenter';
import { ActionsWrapper, MenuButton, SearchWrapper, StyledAppBar, StyledToolbar } from './Header.styles';
import { ShoppingCart } from './ShoppingCart';
import { ProfileDropdown } from './ProfileDropdown';
import { LanguageSelect } from './LanguageSelect';
import { SearchBar } from './SearchBar';
import { isMobileApp } from 'utils/common';
import { closeIcon } from 'assets/svgIcons';
import { useDispatch, useSelector } from 'react-redux';
import { selectDrawer } from 'store/entities/drawer/selectors';
import { selectMenuOpened } from 'store/entities/dashboard/selectors';
import { setOpenMenu, closeMenu } from 'store/entities/dashboard/actions';
import type { GetViewByTypeRes } from 'types';
import { uiStateActions } from 'store/entities/ui';
import { Chatbot } from '../Chatbot';
import { FeatureGate } from 'components/common/Posthog/FeatureGate';

interface HeaderProps {
  shoppingCarts?: GetViewByTypeRes['views'];
}

export const Header = memo(({ shoppingCarts }: HeaderProps) => {
  const mobileApp = isMobileApp();
  const dispatch = useDispatch();
  const drawer = useSelector(selectDrawer());
  const open = useSelector(selectMenuOpened());
  const handleMobileMenuClick = useCallback(() => {
    dispatch(open ? closeMenu() : setOpenMenu());
  }, [dispatch, open]);
  const { id: moduleId } = useParams<{ id: string }>();
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');
  const hasShoppingCart = moduleId && shoppingCarts && shoppingCarts.length > 0 && shoppingCarts.some((cart) => cart.moduleId === moduleId);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const height = ref.current.getBoundingClientRect().height;
      dispatch(uiStateActions.setComponentHeight({ id: 'header', height }));
    }

    return () => {
      dispatch(uiStateActions.removeComponentHeight('header'));
    };
  }, [dispatch]);

  return (
    <StyledAppBar ref={ref} position="fixed" open={open} mobileApp={mobileApp} isDrawerOpen={drawer.isOpen} data-testid="user-dashboard-header">
      <StyledToolbar>
        {mobileApp && (
          <MenuButton onClick={handleMobileMenuClick}>
            <img src={closeIcon} alt="" />
          </MenuButton>
        )}
        {!mobileApp && (
          <SearchWrapper>
            <SearchBar />
          </SearchWrapper>
        )}
        <ActionsWrapper>
          <FeatureGate flag="chatbot_agent_v2">{isAdminPath && <Chatbot />}</FeatureGate>
          <NotificationsCenter testId={isAdminPath ? 'admin-toolbar-notifications-button' : undefined} />
          {hasShoppingCart && <ShoppingCart shoppingCarts={shoppingCarts} />}
          <ProfileDropdown />
          <LanguageSelect />
        </ActionsWrapper>
      </StyledToolbar>
    </StyledAppBar>
  );
});
